import { useState, useMemo } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import SimpleDataGrid from './SimpleDataGrid';
import InputFieldCustom from './InputFieldCustom';
import { Style } from './Styles.jsx';

// Mismo azul del proyecto que ya usa AccountPicker.jsx (ver el comentario de esa
// constante ahi -- se repite literal aca por el mismo motivo).
const PROJECT_BLUE = '#191268';

const formatDate = (value) => value ? new Date(value).toLocaleDateString('es-GT') : '';

const formatMoney = (value) =>
    value === null || value === undefined || value === ''
        ? ''
        : parseFloat(value).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 });

// "Cerrar"/"Elegir" de la ventana de Detalle: Style.DeleteButton/Style.SaveButton (los
// mismos botones de guardar/cancelar que usa el resto de la app) vienen pensados para
// el pie de un formulario completo, con width:100% -- ahi adentro, en una ventana de
// solo lectura mas chica, se ven demasiado grandes con ese ancho. Se probo primero
// reduciendolos ~50% (ancho automatico en vez de 100%) y quedaron muy chicos -- se
// subio despues un 25% ese tamaño (fontSize/padding), manteniendo el ancho automatico
// (que fue lo que además los dejo alineados a la izquierda, en vez de repartidos en
// todo el ancho de la ventana).
const DetailButtonStyle = (base) => ({
    ...base,
    width: 'auto',
    minWidth: 'auto',
    fontSize: '0.875rem',
    padding: '0.375em 1.25em',
});

// Selector de Anticipo con busqueda avanzada -- mismo patron que AccountPicker.jsx
// (lupa que abre una ventana flotante con blooming/tooltip/orden por columna), pero a
// diferencia de aquel este SI hace sus propios llamados de red: la lista de anticipos
// (provider/advances -> sp_lista_anticipos_por_liquidar) no la tiene precargada
// ninguna pantalla, y ademas conviene traerla fresca cada vez que se abre (el
// "monto_por_liquidar" de cada anticipo puede cambiar entre una apertura y otra).
//
// "providerCode": la lista SIEMPRE se filtra al Proveedor de la factura que se esta
// liquidando -- a pedido, no se deja elegir/escribir otro Proveedor desde aca (este
// picker es para elegir un anticipo DE ESE proveedor, no para buscar entre todos). El
// filtro es ademas por "tipo='Proveedor'" (no solo el codigo): sp_lista_anticipos_por_liquidar
// mezcla anticipos de Proveedor y de Reembolsante en la misma columna "codigo_proveedor",
// que son catalogos distintos -- sin este segundo chequeo, un Reembolsante con el mismo
// numero de codigo que el Proveedor de esta factura se colaria en la lista.
//
// "invoiceId": la factura contra la que se va a reservar el anticipo elegido -- ver
// handleChoose mas abajo, que llama a POST provider/invoice/{invoiceId}/advance-assignment
// (AssignAdvanceToInvoice, Providersapi.cs) apenas se hace click en "Elegir".
//
// onApplied() se llama SIN argumentos apenas la reserva ya se hizo en el backend (a
// diferencia de la version anterior de este componente, que le pasaba al padre la fila
// elegida via onSelect ANTES de que existiera ningun llamado de red): el consumidor
// (ProviderPayableInvoiceShow.jsx) no necesita que le pasen nada aca, solo volver a pedir
// la lista de anticipos ya aplicados a la factura (GET provider/invoice/{id}/advances) y el
// Saldo actualizado de la factura (ambos ya cambiaron en el backend para cuando esto se
// llama).
//
// A pedido, ya no se puede "Elegir" directo desde la lista: hay que entrar al Detalle
// (haciendo click en la fila) y elegir el monto a aplicar ahi -- ver handleView/handleChoose
// y la ventana de Detalle mas abajo.
//
// "invoiceBalance": el Saldo pendiente (numerico, balanceValue) de la factura que se esta
// liquidando -- ver ProviderPayableInvoiceShow.jsx. Ademas de lo disponible del propio
// anticipo (amountToSettle), el "Monto a Aplicar" tampoco puede superar este valor: antes
// no se validaba aca, asi que se podia escribir/"Elegir" un monto mayor al Saldo de la
// factura, y recien se rechazaba en el backend (el trigger de cbfacturacionamortizacion),
// con un error generico. Ver chosenAmountMax mas abajo.
const AnticipoPicker = ({ providerCode, invoiceId, invoiceBalance, onApplied, disabled }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    // Catalogo de Moneda para el filtro (una lista, no texto libre -- a pedido). Reutiliza
    // el mismo endpoint "currency" (Get_Currency -> sp_lista_monedas) que ya usa
    // ProviderPayableInvoiceShow.jsx para el combo de Moneda de la Liquidacion -- no hizo
    // falta un catalogo nuevo, solo se le agrego el campo "symbol" al backend (ver
    // Currenciesapi.cs), que el modelo SP_Monedas ya tenia declarado pero no se llenaba.
    // Se necesita el simbolo (no el codigo) porque sp_lista_anticipos_por_liquidar solo
    // trae el simbolo de moneda de cada anticipo ("currencySymbol"), no su codigo.
    const [currencies, setCurrencies] = useState([]);
    const [currencyFilter, setCurrencyFilter] = useState('');

    // Ventana de detalle ("Ver", ahora la UNICA forma de elegir un anticipo -- se abre al
    // hacer click en la fila, ver handleView mas abajo): viewRow es la fila de la LISTA (la
    // que ya tiene amountToSettle) sobre la que se pidio el detalle; viewData es la
    // respuesta de sp_devuelve_registro_anticipo (Get_Provider_Advance) para esa misma fila.
    const [viewOpen, setViewOpen] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewRow, setViewRow] = useState(null);
    const [viewData, setViewData] = useState(null);
    // Monto a Aplicar (campo nuevo, a pedido): cuanto de este anticipo se va a aplicar a la
    // factura -- puede ser parcial, no obliga a usar todo el "amountToSettle" disponible.
    // Se precarga con el disponible completo al abrir el Detalle (ver handleView) y el
    // usuario lo puede editar hacia abajo desde ahi.
    const [chosenAmount, setChosenAmount] = useState('');
    // Loading del boton "Elegir" mientras se llama a advance-assignment (ver handleChoose) --
    // separado de viewLoading (que es solo la carga del detalle) porque este cubre el
    // llamado de red que de verdad reserva el anticipo, no solo consultarlo.
    const [choosing, setChoosing] = useState(false);

    const loadAdvances = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('provider/advances', { filter: {}, pagination: {}, sort: {} });
            setRows(data);
        } catch (error) {
            notify(error?.message || 'No se pudo obtener la lista de anticipos.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadCurrencies = async () => {
        try {
            const { data } = await dataProvider.getList('currency', { filter: {}, pagination: {}, sort: {} });
            setCurrencies(data.map((item) => ({ key: item.id, value: item.name, symbol: item.symbol })));
        } catch (error) {
            notify(error?.message || 'No se pudo obtener el catálogo de monedas.', { type: 'error' });
        }
    };

    const handleOpen = () => {
        setCurrencyFilter('');
        setOpen(true);
        loadAdvances();
        // El catalogo de Moneda no cambia entre una apertura y otra del picker (a
        // diferencia de la lista de anticipos): alcanza con traerlo una sola vez.
        if (currencies.length === 0) {
            loadCurrencies();
        }
    };

    const handleClose = () => setOpen(false);

    const filteredRows = useMemo(() => {
        const providerRows = rows.filter((row) => (
            String(row.providerCode ?? '') === String(providerCode ?? '') && row.advanceType === 'Proveedor'
        ));
        if (!currencyFilter) return providerRows;
        const selectedCurrency = currencies.find((item) => String(item.key) === String(currencyFilter));
        if (!selectedCurrency) return providerRows;
        return providerRows.filter((row) => (row.currencySymbol ?? '').trim() === (selectedCurrency.symbol ?? '').trim());
    }, [rows, providerCode, currencyFilter, currencies]);

    // "row" es la fila de la lista (sp_lista_anticipos_por_liquidar); "chosenAmountValue" es
    // el string crudo del campo "Monto a Aplicar" de la ventana de Detalle -- se limpia igual
    // que el resto de los campos "money" del proyecto (ver amount/handleSettleInvoice en
    // ProviderPayableInvoiceShow.jsx).
    //
    // A pedido, la reserva se hace en este mismo momento (no mas adelante, junto con
    // "Liquidar"): se llama a advance-assignment y recien si eso sale bien se cierra el
    // picker. Si falla (por ejemplo porque el trigger de cbfacturacionamortizacion rechaza el
    // monto por superar lo que le queda pendiente a la factura, algo que puede pasar si ya se
    // aplicaron otros anticipos mientras tanto), se deja todo abierto para que el usuario
    // pueda ajustar el monto o cancelar.
    const handleChoose = async (row, chosenAmountValue) => {
        const parsedChosenAmount = parseFloat((chosenAmountValue ?? '').toString().replace(/[^0-9.-]/g, ''));
        if (isNaN(parsedChosenAmount) || parsedChosenAmount <= 0) {
            return;
        }

        setChoosing(true);
        try {
            await dataProvider.create(`provider/invoice/${invoiceId}/advance-assignment`, {
                data: {
                    advanceCorrelativo: row.correlativo,
                    advanceAmount: parsedChosenAmount.toString(),
                },
            });
            notify('Anticipo aplicado correctamente.', { type: 'success' });
            setViewOpen(false);
            setOpen(false);
            onApplied();
        } catch (error) {
            notify(error?.message || 'No se pudo aplicar el anticipo.', { type: 'error' });
        } finally {
            setChoosing(false);
        }
    };

    const handleView = async (row) => {
        setViewRow(row);
        setViewData(null);
        // Se precarga con el disponible completo del anticipo (amountToSettle) -- el usuario
        // lo puede bajar desde el Detalle si solo quiere aplicar una parte.
        setChosenAmount(formatMoney(row.amountToSettle));
        setViewOpen(true);
        setViewLoading(true);
        try {
            const { data } = await dataProvider.getOne('provider/advances', { id: row.correlativo });
            setViewData(data);
        } catch (error) {
            notify(error?.message || 'No se pudo obtener el detalle del anticipo.', { type: 'error' });
        } finally {
            setViewLoading(false);
        }
    };

    const handleCloseView = () => setViewOpen(false);

    // Ya no hay columnas de "Ver"/"Elegir" -- a pedido, la unica forma de entrar al detalle
    // (y de ahi elegir el anticipo) es haciendo click sobre la fila (ver onRowClick en el
    // <SimpleDataGrid> de la lista, mas abajo).
    const columns = [
        { key: 'advanceDate', name: 'Fecha', render: (value) => formatDate(value) },
        { key: 'amount', name: 'Monto', render: (value) => formatMoney(value) },
        { key: 'amountToSettle', name: 'Por Liquidar', render: (value) => formatMoney(value) },
    ];

    // Monto a Aplicar: no puede ser mayor al disponible del anticipo (amountToSettle) ni
    // menor o igual a 0 -- mismo criterio (tolerancia de 0.005 para redondeo de centavos) que
    // ya usa amountExceedsBalance en ProviderPayableInvoiceShow.jsx.
    const chosenAmountAvailable = viewRow ? (parseFloat(viewRow.amountToSettle) || 0) : 0;
    // Tope real: el menor entre lo disponible del propio anticipo y el Saldo pendiente de la
    // factura (invoiceBalance, ver el comentario de arriba) -- invoiceBalance null/undefined
    // (factura sin terminar de cargar) no agrega ningun limite extra.
    const chosenAmountMax = (invoiceBalance !== null && invoiceBalance !== undefined)
        ? Math.min(chosenAmountAvailable, invoiceBalance)
        : chosenAmountAvailable;
    const parsedChosenAmount = chosenAmount ? parseFloat(chosenAmount.toString().replace(/[^0-9.-]/g, '')) : NaN;
    const chosenAmountInvalid = isNaN(parsedChosenAmount) || parsedChosenAmount <= 0 || parsedChosenAmount > chosenAmountMax + 0.005;

    // Intercepta cada llamada a setValue que hace InputFieldCustom para "Monto a Aplicar"
    // (dos veces por cada tecla cuando "money" esta activo -- ver el mismo comentario en
    // setAmountGuarded de ProviderPayableInvoiceShow.jsx/AccountingExpenseCreate.jsx). A
    // pedido, ya no se deja el campo en un valor invalido con un texto de error aparte
    // debajo (como antes): si lo escrito supera el tope (chosenAmountMax), avisa con una
    // notificacion -- igual que el resto de los errores de monto de la aplicacion -- y
    // ajusta el campo a ese maximo.
    const setChosenAmountGuarded = (rawValue) => {
        const enteredAmount = parseFloat(String(rawValue).replace(/[^0-9.-]/g, '')) || 0;
        if (enteredAmount > chosenAmountMax + 0.005) {
            const limitedByInvoice = invoiceBalance !== null && invoiceBalance !== undefined && invoiceBalance < chosenAmountAvailable;
            notify(
                `El monto (Q ${enteredAmount.toFixed(2)}) supera ${limitedByInvoice ? 'el saldo pendiente de la factura' : 'lo disponible de este anticipo'} (Q ${chosenAmountMax.toFixed(2)}). Se ajustó al máximo disponible.`,
                { type: 'warning' }
            );
            setChosenAmount(chosenAmountMax.toFixed(2));
            return;
        }
        setChosenAmount(rawValue);
    };

    return (
        <>
            {/* El marginLeft negativo (-1.7em) que se copio de AccountPicker.jsx aca no aplicaba:
                ese ajuste compensa el padding de dos columnas de bootstrap contiguas (input col-9
                + boton col-3), pero AnticipoPicker ya no vive adentro de InputFieldCustom -- ahora
                el consumidor (ProviderPayableInvoiceShow.jsx) lo pone directo despues de un
                <label>, y ese margen negativo hacia que la lupa quedara encima del texto de la
                etiqueta. Un margen positivo chico alcanza para separarla sin pegarla. */}
            <div style={{ marginLeft: '0.4em', display: 'inline-block' }}>
                <IconButton onClick={handleOpen} disabled={disabled} style={{ color: PROJECT_BLUE, padding: '4px' }} aria-label="Buscar anticipo" size="small">
                    <SearchIcon />
                </IconButton>
            </div>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ style: { borderRadius: '12px' } }}
            >
                <div style={{ margin: '10px', border: `2px solid ${PROJECT_BLUE}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <DialogTitle><h3 style={{ fontFamily: 'Gothic', color: PROJECT_BLUE }}>Anticipos por Liquidar</h3></DialogTitle>
                    <DialogContent>
                        <style>{`
                            .anticipo-picker-filter .MuiInputBase-input,
                            .anticipo-picker-filter .MuiSelect-select,
                            .anticipo-picker-filter .MuiInputLabel-root,
                            .anticipo-picker-filter .MuiFormLabel-root {
                                font-family: 'Gothic' !important;
                            }
                        `}</style>
                        <div class="row" style={{ paddingBottom: '1em' }}>
                            {/* Moneda como filtro de LISTA (no texto libre, a pedido) -- las
                                opciones salen del mismo catalogo "currency" que ya usa
                                ProviderPayableInvoiceShow.jsx, matcheado contra el simbolo de cada
                                anticipo (ver filteredRows). No hay filtro de Proveedor: la lista ya
                                viene fija al proveedor de la factura. */}
                            <div class="col-4">
                                <TextField
                                    select
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    className="anticipo-picker-filter"
                                    label="Moneda"
                                    value={currencyFilter}
                                    onChange={(e) => setCurrencyFilter(e.target.value)}
                                    InputLabelProps={{ style: { fontFamily: 'Gothic' } }}
                                    // El "select" de MUI no pinta su texto a traves del <input> nativo
                                    // (a diferencia de un TextField comun) sino de un <div
                                    // class="MuiSelect-select">, que el <style> de arriba tambien
                                    // apunta -- pero para que gane siempre (mismo motivo que en
                                    // AccountPicker.jsx: esta version de MUI a veces le da mas peso a
                                    // sus propias clases que a un CSS externo) se fuerza ademas por
                                    // style en linea directo sobre ese div via SelectProps.
                                    SelectProps={{ style: { fontFamily: 'Gothic' } }}
                                >
                                    <MenuItem value="" style={{ fontFamily: 'Gothic' }}>Todas las monedas</MenuItem>
                                    {currencies.map((item) => (
                                        <MenuItem key={item.key} value={item.key} style={{ fontFamily: 'Gothic' }}>{item.value}</MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        </div>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '2em' }}>
                                <CircularProgress size="3rem" />
                            </div>
                        ) : (
                            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                                {/* Ya no hay botones "Ver"/"Elegir" por fila (ver comentario de
                                    columns arriba) -- onRowClick abre el Detalle de esa fila, que es
                                    ahora la unica forma de elegir un anticipo. */}
                                <SimpleDataGrid
                                    columns={columns}
                                    rows={filteredRows}
                                    sortable
                                    centerContent
                                    onRowClick={(_index, row) => handleView(row)}
                                />
                            </div>
                        )}
                    </DialogContent>
                </div>
            </Dialog>
            <Dialog
                open={viewOpen}
                onClose={handleCloseView}
                maxWidth="md"
                fullWidth
                PaperProps={{ style: { borderRadius: '12px' } }}
            >
                <div style={{ margin: '10px', border: `2px solid ${PROJECT_BLUE}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <DialogTitle><h3 style={{ fontFamily: 'Gothic', color: PROJECT_BLUE }}>Detalle de Anticipo</h3></DialogTitle>
                    <DialogContent>
                        {viewLoading ? (
                            <div style={{ textAlign: 'center', padding: '2em' }}>
                                <CircularProgress size="3rem" />
                            </div>
                        ) : (
                            // Mismo criterio que el resto de las pantallas de "Show" del proyecto
                            // (ej. ProvidersShow.jsx): InputFieldCustom readOnly, 3 campos col-4 por
                            // fila, con el borde gris que ese componente ya trae por defecto -- antes
                            // esta ventana armaba las filas a mano con <span> sueltos, sin ese borde,
                            // que es justo lo que se pidio corregir. "Monto a Aplicar" (ultimo campo)
                            // es el unico NO readOnly: el campo nuevo para elegir cuanto de este
                            // anticipo aplicar a la factura (puede ser parcial).
                            <>
                                <div class="row">
                                    <InputFieldCustom readOnly value={viewData?.companyName} col="col-4" width="95%" label="Empresa" type="text" />
                                    <InputFieldCustom readOnly value={`${viewData?.providerCode ?? ''}${viewData?.providerName ? ` - ${viewData.providerName}` : ''}`} col="col-4" width="95%" label="Proveedor" type="text" />
                                    <InputFieldCustom readOnly value={formatDate(viewData?.advanceDate)} col="col-4" width="95%" label="Fecha de Anticipo" type="text" />
                                </div>
                                <div class="row">
                                    <InputFieldCustom readOnly value={formatMoney(viewData?.amount)} col="col-4" width="95%" label="Monto" type="text" />
                                    <InputFieldCustom readOnly value={viewData?.currencyCode ?? ''} col="col-4" width="95%" label="Moneda (código)" type="text" />
                                    <InputFieldCustom readOnly value={viewData?.referenceRate ?? ''} col="col-4" width="95%" label="Tasa de Referencia" type="text" />
                                </div>
                                <div class="row">
                                    <InputFieldCustom readOnly value={viewData?.accountCode ?? ''} col="col-4" width="95%" label="Cuenta (código)" type="text" />
                                    <InputFieldCustom readOnly value={viewData?.paymentMethodCode ?? ''} col="col-4" width="95%" label="Tipo de Movimiento (código)" type="text" />
                                    <InputFieldCustom readOnly value={viewData?.documentNumber ?? ''} col="col-4" width="95%" label="No. de Documento" type="text" />
                                </div>
                                <div class="row">
                                    <InputFieldCustom readOnly value={viewData?.concept || ''} col="col-4" width="95%" label="Concepto" type="text" />
                                    {/* amountToSettle no viene de sp_devuelve_registro_anticipo -- se
                                        completa con el dato que ya se tenia de la lista (viewRow), no
                                        hace falta un segundo llamado para esto. */}
                                    <InputFieldCustom readOnly value={viewRow ? formatMoney(viewRow.amountToSettle) : ''} col="col-4" width="95%" label="Por Liquidar" type="text" />
                                    <InputFieldCustom
                                        value={chosenAmount}
                                        setValue={setChosenAmountGuarded}
                                        col="col-4"
                                        width="95%"
                                        placeholder="Ingresa el monto"
                                        label="Monto a Aplicar (Q) *"
                                        required
                                        type="text"
                                        money
                                    />
                                </div>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button style={DetailButtonStyle(Style.DeleteButton)} onClick={handleCloseView} disabled={choosing}>Cerrar</Button>
                        <Button style={DetailButtonStyle(Style.SaveButton)} disabled={viewLoading || choosing || !viewRow || chosenAmountInvalid} onClick={() => handleChoose(viewRow, chosenAmount)}>{choosing ? 'Aplicando...' : 'Elegir'}</Button>
                    </DialogActions>
                </div>
            </Dialog>
        </>
    );
};

export default AnticipoPicker;
